import {
  Component,
  Host,
  h,
  Element,
  Prop,
  State,
  Watch,
  Event,
  EventEmitter
} from "@stencil/core";

import {
  StzhStickyStuckTopEvent,
  StzhStickyStuckBottomEvent
} from "../../index";

import { isVisible, offset } from "../../utils/utils";
import throttle from "lodash/throttle";

const ATTR_STICKY_CONTAINER = "data-stzh-sticky-container";
const ATTR_STICKY_WIDTH_CONTAINER = "data-stzh-sticky-width-container";

/**
 * @slot - Slot for any content 
 */
@Component({
  tag: "stzh-sticky",
  styleUrl: "stzh-sticky.scss",
  shadow: true
})
export class StzhSticky {
  /** Variant */
  @Prop({ reflect: true }) variant: "default" | "transparent" = "default";

  /** Whether to disable sticking to top */
  @Prop({ reflect: true }) disableStickyTop: boolean = true;

  /** Whether to disable sticking to bottom */
  @Prop({ reflect: true }) disableStickyBottom: boolean = false;

  /** Whether background breaks out container to full viewport width */
  @Prop({ reflect: true }) bleeding: boolean = false;

  /** Name of sticky element (used for setting css properties) */
  @Prop() name: string;

  /**
   * Position type used.
   *
   * - sticky: used only css position sticky
   * - fixed: uses position fixed with js for positioning (`data-stzh-sticky-container` can be used on a container element to limit sticky position)
   */
  @Prop({ reflect: true }) position: "sticky" | "fixed" = "sticky";

  @Watch("disableStickyTop")
  @Watch("disableStickyBottom")
  disableStickyWatcher() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (this.position === "fixed") {
          this.updateFixedByResize();
        } else {
          this.updateStuck();
        }
      });
    });
  }

  @Watch("isStuckTop")
  @Watch("isStuckBottom")
  @Watch("hasReachedTopLimit")
  @Watch("hasReachedBottomLimit")
  stuckHeightWatcher() {
    let isStuck = false;

    if (this.isStuckTop && !this.hasReachedBottomLimit) {
      isStuck = true;
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-stuck-top`, "1");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-not-stuck-top`, "0");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}stuck-top-height`, `${this.stickyElement.offsetHeight}px`);
    } else {
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-stuck-top`, "0");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-not-stuck-top`, "1");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}stuck-top-height`, "0px");
    }

    if (this.isStuckBottom && !this.hasReachedTopLimit) {
      isStuck = true;
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-stuck-bottom`, "1");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-not-stuck-bottom`, "0");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}stuck-bottom-height`, `${this.stickyElement.offsetHeight}px`);
    } else {
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-stuck-bottom`, "0");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-not-stuck-bottom`, "1");
      document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}stuck-bottom-height`, "0px");
    }

    document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-stuck`, isStuck ? "1" : "0");
    document.documentElement.style.setProperty(`--stzh-sticky-${this.name && this.name + '-'}is-not-stuck`, isStuck ? "0" : "1");

    if (isStuck) {
      this.stickyResizeObserver.observe(this.stickyElement);
    } else {
      this.stickyResizeObserver.unobserve(this.stickyElement);
    }
  }

  @Watch("isStuckTop")
  isStuckTopWatcher(newValue: boolean) {
    this.stzhStuckTop.emit({
      component: "stzh-sticky",
      sticky: newValue
    });
  }

  @Watch("isStuckBottom")
  isStuckBottomWatcher(newValue: boolean) {
    this.stzhStuckBottom.emit({
      component: "stzh-sticky",
      sticky: newValue
    });
  }

  @Watch("position")
  @Watch("bleeding")
  positionWatcher() {
    if (this.position === "fixed") {
      if (this.observer) {
        this.observer.disconnect();
      }

      window.addEventListener("scroll", this.handleScroll, { passive: true });
      window.addEventListener("resize", this.handleResize);

      this.updateFixedByResize();
    } else {
      if (this.bleeding) {
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        this.updateByScroll();
      } else {
        window.removeEventListener("scroll", this.handleScroll);
      }

      window.removeEventListener("resize", this.handleResize);
      this.setUnfixed();

      this.observer = new IntersectionObserver(this.observe, { threshold: [1] });
      this.observer.observe(this.stickyElement);

      this.updateStuck();
    }
  }

  /** Sticky stuck top event */
  @Event() stzhStuckTop: EventEmitter<StzhStickyStuckTopEvent>;

  /** Sticky stuck bottom event */
  @Event() stzhStuckBottom: EventEmitter<StzhStickyStuckBottomEvent>;

  @Element() element: HTMLStzhStickyElement;

  @State() isStuckTop: boolean;
  @State() isStuckBottom: boolean;

  @State() hasReachedTopLimit: boolean;
  @State() hasReachedBottomLimit: boolean;

  @State() loaded: boolean = false;

  private observer: IntersectionObserver;
  private stickyResizeObserver: ResizeObserver;
  private debounceScroll: number;
  private debounceResize: number;
  private stickyElement: HTMLElement;
  private spacerElement: HTMLElement;

  private windowHeight: number;
  private scrollX: number;
  private scrollY: number;
  private fixedTop: number;
  private fixedLeft: number;
  private fixedHeight: number = 0;
  private fixedWidth: number = 0;
  private fixedLimit: { top: number, bottom: number } | null = null;

  private updateStuck() {
    const topCss = parseInt(getComputedStyle(this.stickyElement).top, 10);
    const bottomCss = parseInt(getComputedStyle(this.stickyElement).bottom, 10);
    const rect = this.stickyElement.getBoundingClientRect();

    if (topCss) {
      this.isStuckTop = rect.top < 0;
    } else {
      this.isStuckTop = false;
    }

    if (bottomCss) {
      this.isStuckBottom = document.documentElement.clientHeight < rect.bottom;
    } else {
      this.isStuckBottom = false;
    }
  }

  private getFixedLimit(): { top: number, bottom: number } | null {
    const stickyContainer = this.element.closest(`[${ATTR_STICKY_CONTAINER}]`);

    if (!stickyContainer) {
      return null;
    }

    const off = offset(stickyContainer);

    return {
      top: off.top,
      bottom: off.top + stickyContainer.scrollHeight,
    };
  }

  private setUnfixed() {
    Object.assign(this.stickyElement.style, {
      position: null,
      top: null,
      bottom: null,
      left: null,
      width: null,
    });

    Object.assign(this.spacerElement.style, {
      display: "none",
    });
  }

  private setFixed() {
    const position = (this.hasReachedTopLimit || this.hasReachedBottomLimit)
      ? "absolute"
      : "fixed";

    Object.assign(this.stickyElement.style, {
      position,
      top: position === "absolute"
        ? `${this.isStuckTop ? this.fixedLimit.bottom - this.fixedHeight : this.fixedLimit.top}px`
        : this.isStuckTop ? "0px" : "auto",
      bottom: this.isStuckTop || position === "absolute"
        ? "auto" : "0px",
      left: `${this.fixedLeft + (position === "fixed" ? this.scrollX : 0)}px`,
      width: `${this.fixedWidth}px`,
    });

    Object.assign(this.spacerElement.style, {
      display: "block",
    });
  }

  private handleScroll = () => {
    if (this.debounceScroll) {
      window.cancelAnimationFrame(this.debounceScroll);
    }

    this.debounceScroll = requestAnimationFrame(this.updateByScroll);
  }

  private handleResize = () => {
    if (this.debounceResize) {
      window.cancelAnimationFrame(this.debounceResize);
    }

    this.debounceResize = requestAnimationFrame(this.updateFixedByResize);
  }

  private handleStickyResize = throttle(() => {
    this.stuckHeightWatcher();
    this.updateByScroll();
  }, 100);

  private updateByScroll = () => {
    if (!isVisible(this.stickyElement) || !this.element.isConnected) {
      return;
    }

    const stickyFixedWidthContainer = this.element.closest(`[${ATTR_STICKY_WIDTH_CONTAINER}]`) as HTMLElement
      || this.element.parentElement;

    const off = offset(
      (this.isStuckTop || this.isStuckBottom) && this.position === "fixed"
      ? this.spacerElement : this.stickyElement
    );

    this.fixedTop = off.top;
    this.fixedLeft = off.left;

    stickyFixedWidthContainer.style.setProperty('--stzh-sticky-offset-top', `${this.fixedTop}px`);
    stickyFixedWidthContainer.style.setProperty('--stzh-sticky-offset-left', `${this.fixedLeft}px`);

    if (this.position === "sticky") {
      // for position sticky we only need the offsets variables to be updated in bleeding variant
      return;
    }

    this.fixedHeight = this.stickyElement.offsetHeight;
    this.spacerElement.style.height = `${this.fixedHeight}px`;

    this.scrollX = window.scrollX;
    this.scrollY = window.scrollY;
    this.fixedLimit = this.getFixedLimit();

    this.fixedWidth = stickyFixedWidthContainer.getBoundingClientRect().width;

    const unstuckWidth = this.element.getBoundingClientRect().width;
    // stickyFixedWidthContainer.style.setProperty('--stzh-sticky-stuck-width', `${this.fixedWidth}px`);
    stickyFixedWidthContainer.style.setProperty('--stzh-sticky-unstuck-width', `${unstuckWidth}px`);

    // console.log(unstuckWidth);

    // console.log(
    //   this.scrollX,
    //   this.scrollY,
    //   this.windowHeight,
    //   this.fixedTop,
    //   this.fixedLeft,
    //   this.fixedHeight,
    //   this.fixedLimit,
    // );

    this.isStuckBottom = !this.disableStickyBottom
      && this.scrollY + this.windowHeight <= this.fixedTop + this.fixedHeight;
    this.isStuckTop = !this.disableStickyTop
      && this.scrollY >= this.fixedTop;

    this.hasReachedTopLimit = this.fixedLimit
      && this.scrollY + this.windowHeight - this.fixedHeight <= this.fixedLimit.top;
    this.hasReachedBottomLimit = this.fixedLimit
      && this.scrollY >= this.fixedLimit.bottom - this.fixedHeight;

    // console.log(
    //   this.isStuckBottom,
    //   this.isStuckTop,
    //   this.hasReachedBottomLimit,
    //   this.hasReachedTopLimit,
    // );

    if (this.isStuckTop || this.isStuckBottom) {
      this.setFixed();
      // stickyFixedWidthContainer.style.setProperty('--stzh-sticky-width', `${this.fixedWidth}px`);
    } else {
      this.setUnfixed();
      // stickyFixedWidthContainer.style.setProperty('--stzh-sticky-width', `${unstuckWidth}px`);
    }
  }

  private updateFixedByResize = () => {
    if (!this.stickyElement || !this.element.isConnected) {
      return;
    }

    // reset sticky so we can read current offsets
    this.setUnfixed();
    this.stickyElement.style.position = "static";
    this.stickyElement.style.width = "auto";
    this.isStuckTop = false;
    this.isStuckBottom = false;

    if (isVisible(this.stickyElement)) {
      this.windowHeight = window.innerHeight;
      this.updateByScroll();
    }
  }

  private observe = () => {
    this.updateStuck();
  };

  componentDidLoad() {
    this.stickyResizeObserver = new ResizeObserver(this.handleStickyResize);
    this.positionWatcher();

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.setTimeout(() => {
          this.loaded = true;

          if (this.position === "fixed") {
            this.updateFixedByResize();
          }
        }, 250);
      });
    });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.stickyResizeObserver?.disconnect();

    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
  }

  render() {
    const isStuck = this.isStuckTop || this.isStuckBottom;

    const classes = {
      "stzh-sticky": true,
      "stzh-sticky--is-not-loaded": !this.loaded,
      "stzh-sticky--is-stuck": isStuck,
      "stzh-sticky--is-stuck-top": this.isStuckTop,
      "stzh-sticky--is-stuck-bottom": this.isStuckBottom,
      "stzh-sticky--is-disabled-sticky-top": this.disableStickyTop,
      "stzh-sticky--is-disabled-sticky-bottom": this.disableStickyBottom,
      "stzh-sticky--is-bleeding": this.bleeding,
      [`stzh-sticky--position-${this.position}`]: !!this.position,
      [`stzh-sticky--${this.variant}`]: !!this.variant,
    };

    return (
      <Host
        is-stuck={this.isStuckTop || this.isStuckBottom}
        is-stuck-top={this.isStuckTop}
        is-stuck-bottom={this.isStuckBottom}
      >
        <div class={classes}>
          <div
            ref={(el) => (this.spacerElement = el as HTMLDivElement)}
            class="stzh-sticky__spacer"
          ></div>
          <div
            ref={(el) => (this.stickyElement = el as HTMLDivElement)}
            class="stzh-sticky__sticky"
          >
            <div class="stzh-sticky__content">
              <div class="stzh-sticky__content-inner">
                <slot></slot>
              </div>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
